import cloneDeep from 'lodash/cloneDeep';
import {arrayMove} from '@dnd-kit/helpers';

import {eventPathContainsSelectors} from 'common/util/dom';

import {DragEndEvent, DragMetadata, EntryGenerator, EntrySetter, OnBeforeDragEndEvent} from './types';

/**
 * Handles the reordering or cloning of entries in a draggable list based on a drag-and-drop event.
 *
 * This function processes the drag end event, determines whether the operation is a move or a clone (using the Alt key),
 * and updates the entries array accordingly. It uses an optional `entryGenerator` to create new entries when cloning,
 * and calls the provided `setEntries` function to update the state.
 *
 * @param params - The parameters for handling the reorder event.
 * @param params.event - The drag end event from `@dnd-kit/react`.
 * @param params.entries - The current array of entries to be reordered or cloned.
 * @param params.entryGenerator - Optional function to generate a new entry when cloning.
 * @param params.setEntries - Function to update the entries array after reordering or cloning.
 *
 * @remarks
 * - If the drag operation is canceled, or if required data is missing, the function returns early.
 * - When cloning (Alt key pressed), a deep copy of the dragged entry is inserted at the source index.
 * - When moving, the entry is moved from the source index to the target index, adjusting for the direction of the move.
 * - The `setEntries` function receives both the updated entries and metadata about the operation.
 */
export const handleListReorder = function <T extends Obj = Obj>({
  event,
  entries,
  entryGenerator,
  setEntries,
}: {
  event: DragEndEvent;
  entryGenerator?: EntryGenerator<T>;
  entries: T[];
  setEntries: EntrySetter<T>;
}) {
  const targetData = event.operation.target?.data as DragMetadata;
  const sourceData = event.operation.source?.data as DragMetadata;
  const isCloning = Boolean((event.nativeEvent as MouseEvent)?.altKey);

  if (event.operation.canceled || !entries || !sourceData || !targetData) {
    return;
  }

  const from = sourceData.index;
  const targetIndex = targetData.index;

  // adjust the drop target index to account for the drop target position relative to the source
  const modifier = from > targetIndex ? 0 : -1;
  const to = Math.max(targetIndex + modifier, 0);

  // only move items if shifting the list
  if ((from + 1 === targetIndex || from === targetIndex) && !isCloning) {
    return;
  }

  const draggedEntry = entries[from];
  const newEntry = cloneDeep(entryGenerator ? entryGenerator(draggedEntry, {isCloning}) : draggedEntry);

  if (!newEntry) {
    return;
  }

  let updatedEntries = cloneDeep(entries);
  if (isCloning) {
    updatedEntries.splice(from === to ? to + 1 : to, 0, newEntry);
  } else {
    updatedEntries = arrayMove(entries, from, to);
  }

  setEntries(updatedEntries, {
    oldEntries: cloneDeep(entries),
    from,
    to,
  });
};

/**
 * Handler for the `onBeforeDragStart` event on a DragDropProvider.
 * This prevents unwanted drag operations from within a dropdown or interactive elements
 */
export function handleOnBeforeDrag(event: OnBeforeDragEndEvent) {
  const {activatorEvent} = event.operation;

  if (
    activatorEvent &&
    (activatorEvent.target instanceof HTMLInputElement ||
      eventPathContainsSelectors(activatorEvent, [`mp-drop-menu`, `mp-input`]).containsSelectors)
  ) {
    // Prevent the drag operation from starting
    event.preventDefault();
  }
}
